<!--
  @description: 手动发送通知组件
  @author: system
  @date: 2026-06-02
  @note: 手动向用户发送微信公众号模版消息
-->
<template>
  <div class="manual-send-container">
    <!-- 提示信息 -->
    <el-alert
      title="手动发送通知说明"
      type="info"
      description="请选择公众号、用户和消息模版，填写模版字段后即可发送通知"
      :closable="false"
      show-icon
      style="margin-bottom: 20px;"
    ></el-alert>

    <!-- 发送表单 -->
    <div class="form-container">
      <el-form ref="form" :model="dialogForm" label-width="120px" class="send-form">
        <el-form-item label="选择公众号" required>
          <el-select
            v-model="dialogForm.appid"
            placeholder="请选择公众号"
            clearable
            filterable
            style="width:100%"
            @change="changeAppid"
          >
            <el-option
              v-for="item in gzhList"
              :key="item.appid"
              :label="item.account_name"
              :value="item.appid"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="选择用户" required>
          <el-select
            v-model="dialogForm.gzh_openid_list"
            placeholder="请先选择公众号，再选择用户（可多选）"
            multiple
            filterable
            clearable
            style="width:100%"
            :disabled="!dialogForm.appid"
          >
            <el-option
              v-for="(item, index) in userList"
              :key="index"
              :label="item.display_name"
              :value="item.gzh_openid"
            >
              <span style="float: left">{{ item.user_name || '未命名' }}</span>
              <span style="float: right; color: #8492a6; font-size: 12px">{{ item.gzh_openid }}</span>
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="选择模版ID" required>
          <el-select
            v-model="dialogForm.template_id"
            placeholder="请先选择公众号，再选择消息模版"
            clearable
            filterable
            style="width:100%"
            @change="changeTemplate"
            :disabled="!dialogForm.appid"
          >
            <el-option
              v-for="(item, index) in templateList"
              :key="index"
              :label="item.name"
              :value="item.id"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="模版内容" required v-if="dialogForm.template_id">
          <div class="template-fields">
            <div
              v-for="(item, index) in dialogForm.template_msg_list"
              :key="index"
              class="field-row"
            >
              <el-tag type="info" size="mini" class="field-tag">字段中文描述:</el-tag>
              <el-input
                size="mini"
                v-model="item.keyName"
                placeholder="请输入"
                class="field-input"
                disabled
              ></el-input>

              <el-tag type="info" size="mini" class="field-tag">字段英文命名:</el-tag>
              <el-input
                size="mini"
                v-model="item.key"
                placeholder="请输入"
                class="field-input"
                disabled
              ></el-input>

              <el-tag type="success" size="mini" class="field-tag">字段值:</el-tag>
              <el-input
                size="mini"
                v-model="item.value"
                placeholder="请输入要发送的内容"
                class="field-value"
                type="textarea"
                :rows="1"
              ></el-input>
            </div>
          </div>
        </el-form-item>

        <el-form-item label>
          <el-button
            size="small"
            type="primary"
            icon="el-icon-s-promotion"
            @click="save"
            v-waves
          >立即发送</el-button>
          <el-button size="small" @click="resetForm">重置</el-button>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
/**
 * @description 手动发送通知组件
 * @component ManualSend
 * @requires services/sendService/wxSend - 发送通知API
 * @requires services/sendService/sendTemplate - 模版相关API
 * @requires services/sendService/sendAccount - 公众号相关API
 * @requires services/sendService/sendVisitor - 用户相关API
 */
import { mapState } from "vuex";
import { wxSendAPi } from "@/services/sendService/wxSend";
import { selectApi as selectTemplateApi } from "@/services/sendService/sendTemplate";
import { selectApi as selectGzhListApi } from "@/services/sendService/sendAccount";
import { selectApi as selectUserApi } from "@/services/sendService/sendVisitor";

export default {
  name: "ManualSend",
  components: {},
  async created() {
    // 组件创建时获取公众号列表
    this.getGzhList();
    // 模版和用户列表在选择公众号后加载
  },
  data() {
    return {
      // 发送表单数据
      dialogForm: {
        appid: "",
        gzh_openid_list: [],
        template_id: "",
        template_msg: [],
        template_msg_list: []
      },
      // 公众号列表
      gzhList: [],
      // 用户列表
      userList: [],
      // 消息模版列表
      templateList: []
    };
  },
  computed: {
    ...mapState(["userInfo"])
  },
  methods: {
    /**
     * @description 获取公众号列表
     */
    async getGzhList() {
      const { data, code } = await selectGzhListApi({
        pageNumber: 1,
        pageSize: 100
      });
      if (code === 200) {
        this.gzhList = data;
      }
    },

    /**
     * @description 选择公众号
     * @param {string} appid - 公众号appid
     */
    changeAppid(appid) {
      // 清空依赖公众号的字段
      this.dialogForm.template_id = '';
      this.dialogForm.template_msg_list = [];
      this.dialogForm.gzh_openid_list = [];
      
      // 重新加载模版和用户列表
      this.getTemplateData(appid);
      this.getUserList(appid);
    },

    /**
     * @description 获取消息模版列表（根据appid筛选）
     */
    async getTemplateData(appid) {
      if (!appid) {
        this.templateList = [];
        return;
      }
      const { data, code } = await selectTemplateApi({
        pageNumber: 1,
        pageSize: 1000,
        appid: appid
      });
      if (code === 200) {
        this.templateList = data.map(item => {
          return {
            ...item,
            name: item.template_name + "(模版ID:" + item.id + ")"
          };
        });
      }
    },

    /**
     * @description 获取用户列表（根据appid筛选）
     */
    async getUserList(appid) {
      if (!appid) {
        this.userList = [];
        return;
      }
      const { data, code } = await selectUserApi({
        pageNumber: 1,
        pageSize: 1000,
        appid: appid
      });
      if (code === 200) {
        this.userList = data.map(item => {
          return {
            ...item,
            display_name: item.user_name 
              ? `${item.user_name}(${item.gzh_openid})` 
              : item.gzh_openid
          };
        });
      }
    },

    /**
     * @description 选择消息模版
     * @param {number} templateId - 模版ID
     */
    changeTemplate(templateId) {
      const item = this.templateList.find(item => item.id === templateId);
      if (item) {
        try {
          this.dialogForm.template_msg_list = JSON.parse(item.template_msg_list);
        } catch (error) {
          this.dialogForm.template_msg_list = [];
        }
      }
    },

    /**
     * @description 将字段数组转化为公众号模版格式的对象
     * @param {Array} array - 字段数组
     * @returns {Object} 转换后的对象
     */
    transformArrayToObject(array) {
      let transformed = {};
      for (let i = 0; i < array.length; i++) {
        const item = array[i];
        const { key, value } = item;
        transformed[key] = { value };
      }
      return transformed;
    },

    /**
     * @description 发送通知
     */
    save() {
      let formData = JSON.parse(JSON.stringify(this.dialogForm));

      // 校验公众号
      if (!formData.appid) {
        this.$message.error("请选择公众号");
        return;
      }

      // 校验用户
      if (!formData.gzh_openid_list || formData.gzh_openid_list.length === 0) {
        this.$message.error("请至少选择一个用户");
        return;
      }

      // 校验模版字段
      for (let i = 0; i < formData.template_msg_list.length; i++) {
        const item = formData.template_msg_list[i];
        if (!item.keyName || !item.key || !item.value) {
          this.$message.error("请完善模版字段");
          return;
        }
      }

      // 转换模版字段格式
      try {
        formData.template_msg = this.transformArrayToObject(
          formData.template_msg_list
        );
      } catch (error) {
        formData.template_msg = null;
      }

      // 必填字段校验
      if (
        !formData.template_id ||
        !formData.template_msg
      ) {
        this.$message.error("请填写必填字段");
        return;
      }

      delete formData.template_msg_list;

      // 批量发送给所有选中的用户
      let successCount = 0;
      let failCount = 0;
      const totalCount = formData.gzh_openid_list.length;

      const sendPromises = formData.gzh_openid_list.map(gzh_openid => {
        return wxSendAPi({
          appid: formData.appid,
          gzh_openid: gzh_openid,
          template_id: formData.template_id,
          template_msg: formData.template_msg
        }).then(res => {
          if (res.code === 200) {
            successCount++;
          } else {
            failCount++;
          }
          return res;
        }).catch(() => {
          failCount++;
        });
      });

      // 等待所有发送完成
      Promise.all(sendPromises).then(() => {
        if (failCount === 0) {
          this.$message.success(`发送成功！共发送${successCount}条通知`);
        } else if (successCount === 0) {
          this.$message.error(`发送失败！共${failCount}条通知发送失败`);
        } else {
          this.$message.warning(`部分成功！成功${successCount}条，失败${failCount}条`);
        }
        // 重置模版字段值
        this.resetTemplateValues();
      });
    },

    /**
     * @description 重置表单
     */
    resetForm() {
      this.dialogForm = {
        appid: "",
        gzh_openid_list: [],
        template_id: "",
        template_msg: [],
        template_msg_list: []
      };
      this.templateList = [];
      this.userList = [];
      this.$refs.form && this.$refs.form.clearValidate();
    },

    /**
     * @description 重置模版字段值（保留模版选择）
     */
    resetTemplateValues() {
      if (this.dialogForm.template_msg_list && this.dialogForm.template_msg_list.length > 0) {
        this.dialogForm.template_msg_list.forEach(item => {
          item.value = "";
        });
      }
    }
  }
};
</script>

<style scoped lang="less">
.manual-send-container {
  padding: 20px 0;
  min-height: calc(100vh - 150px);

  // 表单容器
  .form-container {
    background: #fff;
    border-radius: 12px;
    padding: 30px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);

    /deep/ .el-button--primary {
      background-color: #165DFF;
      border-color: #165DFF;

      &:hover,
      &:focus {
        background-color: #4080FF;
        border-color: #4080FF;
      }
    }

    .send-form {
      max-width: 900px;
      margin: 0 auto;

      // 模版字段区域
      .template-fields {
        border: 1px solid #e4e7ed;
        border-radius: 8px;
        padding: 20px;
        background: #f5f7fa;

        .field-row {
          display: flex;
          align-items: flex-start;
          margin-bottom: 15px;
          gap: 10px;
          flex-wrap: wrap;

          &:last-child {
            margin-bottom: 0;
          }

          .field-tag {
            flex-shrink: 0;
            margin-top: 5px;
          }

          .field-input {
            width: 180px;
          }

          .field-value {
            flex: 1;
            min-width: 250px;
          }
        }
      }
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .manual-send-container {
    .form-container {
      padding: 20px 15px;

      .send-form {
        .template-fields {
          padding: 15px;

          .field-row {
            flex-direction: column;

            .field-input,
            .field-value {
              width: 100% !important;
            }
          }
        }
      }
    }
  }
}
</style>
